import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Building2, Clock, ShieldCheck, ArrowRight } from 'lucide-react';
import { PageHeader } from '@/components/system/PageHeader';
import { SectionCard } from '@/components/system/SectionCard';
import { DataTable, type Column } from '@/components/system/DataTable';
import { EmptyState } from '@/components/system/EmptyState';
import { useIFMeusContratos } from '../hooks/useIFAdminContratos';
import { differenceInDays } from 'date-fns';

const IFMeusContratosPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: contratos = [], isLoading } = useIFMeusContratos();

  const provisorios = contratos.filter((c: any) => c.status === 'provisorio');
  const ativos = contratos.filter((c: any) => c.status === 'ativo');

  const diasRestantes = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const diff = differenceInDays(new Date(expiresAt), new Date());
    return Math.max(0, diff);
  };

  const provisorioColumns: Column<any>[] = [
    { key: 'contrato', label: 'Contrato', render: r => (
      <button onClick={() => navigate(`/financiamento/contrato/${r.contrato_id}`)} className="text-left font-medium text-primary hover:underline">
        {r.contrato_nome}
      </button>
    )},
    { key: 'tipo', label: 'Tipo', align: 'center', render: r => (
      <Badge variant={r.tipo_vinculo === 'financiamento' ? 'default' : 'outline'} className="text-xs">
        {r.tipo_vinculo === 'financiamento' ? 'Financiamento' : 'Monitoramento'}
      </Badge>
    )},
    { key: 'dias', label: 'Dias Restantes', align: 'center', render: r => {
      const dias = diasRestantes(r.expires_at);
      if (dias === null) return '—';
      return (
        <div className="flex items-center justify-center gap-1.5">
          <Clock className="h-3.5 w-3.5" style={{ color: dias <= 3 ? 'hsl(var(--destructive))' : 'hsl(var(--warning))' }} />
          <span className={`font-bold text-sm ${dias <= 3 ? 'text-destructive' : ''}`}>{dias} dias</span>
        </div>
      );
    }},
    { key: 'status', label: 'Status', align: 'center', render: () => (
      <Badge variant="secondary" className="text-xs gap-1">
        <Clock className="h-3 w-3" /> Provisório
      </Badge>
    )},
    { key: 'acao', label: '', align: 'center', render: r => (
      <Button variant="ghost" size="sm" onClick={() => navigate(`/financiamento/contrato/${r.contrato_id}`)}>
        <ArrowRight className="h-4 w-4" />
      </Button>
    )},
  ];

  const ativoColumns: Column<any>[] = [
    { key: 'contrato', label: 'Contrato', render: r => (
      <button onClick={() => navigate(`/financiamento/contrato/${r.contrato_id}`)} className="text-left font-medium text-primary hover:underline">
        {r.contrato_nome}
      </button>
    )},
    { key: 'tipo', label: 'Tipo', align: 'center', render: r => (
      <Badge variant={r.tipo_vinculo === 'financiamento' ? 'default' : 'outline'} className="text-xs">
        {r.tipo_vinculo === 'financiamento' ? 'Financiamento' : 'Monitoramento'}
      </Badge>
    )},
    { key: 'status', label: 'Status', align: 'center', render: () => (
      <Badge variant="default" className="text-xs gap-1">
        <ShieldCheck className="h-3 w-3" /> Ativo
      </Badge>
    )},
    { key: 'acao', label: '', align: 'center', render: r => (
      <Button variant="ghost" size="sm" onClick={() => navigate(`/financiamento/contrato/${r.contrato_id}`)}>
        <ArrowRight className="h-4 w-4" />
      </Button>
    )},
  ];

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
        <PageHeader
          title="Meus Contratos"
          subtitle="Contratos vinculados à sua instituição financeira"
          actions={
            <Button variant="outline" onClick={() => navigate('/financiamento/resgatar-convite')} className="gap-2">
              <ShieldCheck className="h-4 w-4" /> Resgatar Convite
            </Button>
          }
        />

        {contratos.length === 0 ? (
          <EmptyState
            icon={Building2}
            text="Nenhum contrato vinculado. Utilize um token de convite para vincular sua instituição."
          />
        ) : (
          <>
            {provisorios.length > 0 && (
              <SectionCard title={`Acessos Provisórios (${provisorios.length})`} icon={Clock}>
                <div className="mb-3 p-3 rounded-md" style={{ backgroundColor: 'hsl(var(--warning) / 0.1)', border: '1px solid hsl(var(--warning) / 0.3)' }}>
                  <p className="text-sm" style={{ color: 'hsl(var(--warning))' }}>
                    ⚠ Estes acessos são provisórios e expiram automaticamente. O administrador do cluster deve confirmar o vínculo para acesso permanente.
                  </p>
                </div>
                <DataTable columns={provisorioColumns} data={provisorios} keyExtractor={r => r.id} />
              </SectionCard>
            )}

            {ativos.length > 0 && (
              <SectionCard title={`Acessos Ativos (${ativos.length})`} icon={ShieldCheck}>
                <DataTable columns={ativoColumns} data={ativos} keyExtractor={r => r.id} />
              </SectionCard>
            )}
          </>
        )}
    </div>
  );
};

export default IFMeusContratosPage;
